/**
 * Forløbet trin for trin + kravene EFTER kurset — masterskabelonen fra
 * godsvognmand-piloten rullet ud på de øvrige kursusområder.
 *
 * REGLER FOR DENNE FIL:
 *  1. Hver sætning skal kunne føres tilbage til en kilde i `sources`.
 *     Står det ikke i bekendtgørelsen eller hos myndigheden/trafikselskabet,
 *     skriver vi det ikke.
 *  2. Ingen priser og ingen varigheder på Votras egne forløb — de kommer fra
 *     courseGroups.js. Lovkrav til timetal er myndighedsfakta og må stå her.
 */

const FS = (label, path) => ({ label, url: `https://www.fstyr.dk${path}` });

const TAXI_BEK = {
  label: "Taxibekendtgørelsen (BEK nr. 1244 af 26/11/2019)",
  url: "https://www.retsinformation.dk/eli/lta/2019/1244",
};

export const COURSE_PROCESS = {
  taxa: {
    processTitle: "Sådan bliver du taxachauffør hos Votra",
    steps: [
      {
        title: "Du tilmelder dig et hold",
        text: "Du vælger et af de kommende hold på denne side. Votra Danmark ApS er optaget på Færdselsstyrelsens liste over godkendte udbydere af taxi-kvalifikationsuddannelsen, og uddannelsen må kun afholdes af godkendte uddannelsessteder.",
      },
      {
        title: "Du dokumenterer adgangskravene",
        text: "Du skal være fyldt 21 år på det tidspunkt, hvor du aflægger de afsluttende prøver, og have haft kørekort til kategori B i mindst 3 år, perioder, hvor kørekortet har været frakendt, tæller ikke med. Du skal forevise dit gyldige, originale kørekort og dokumentere, at du behersker dansk på niveau med Prøve i Dansk 2 (eller folkeskolens prøve i dansk, 9. klasse).",
      },
      {
        title: "Undervisning, praktik og individuelle køretimer",
        text: "Kvalifikationskurset skal efter taxibekendtgørelsen vare mindst 74 timer, heraf mindst 6 individuelle køretimer med trafiksikkerhed, passagervenlig samt defensiv og energirigtig kørsel. En time er 60 minutter inklusive op til 15 minutters pause. Førstehjælpsdelen udgør 11 timer og følger Dansk Førstehjælpsråds undervisningsplaner for \"Færdselsrelateret førstehjælp\" og \"Førstehjælp ved hjertestop\".",
      },
      {
        title: "De to afsluttende prøver",
        text: "Uddannelsen afsluttes med både en teoretisk og en praktisk prøve. Den teoretiske prøve er udarbejdet af Færdselsstyrelsen og aflægges i styrelsens elektroniske prøveaflæggelsessystem. Den praktiske prøve køres i bil og bedømmes af en godkendt kørelærer til kategori B, med ekstern censur af en kørelærer, der er ansat på et andet godkendt uddannelsessted.",
      },
      {
        title: "Ansøgningen om chaufførkortet sendes gennem uddannelsesstedet",
        text: "Når du har bestået, indgives ansøgningen om chaufførkort digitalt gennem uddannelsesstedet i Færdselsstyrelsens bevisadministrationssystem. Du giver selv samtykke via borger.dk, og der skal vedlægges en lægeattest på styrelsens blanket, som ikke må være over tre måneder gammel. Færdselsstyrelsen udsteder et midlertidigt chaufførkort, så du kan arbejde, mens det endelige kort behandles.",
      },
    ],
    afterTitle: "Kravene til chaufførkortet, dem afgør Færdselsstyrelsen",
    afterIntro:
      "Beviset for den beståede kvalifikationsuddannelse dækker kravet om faglige kvalifikationer. Chaufførkortet udstedes af Færdselsstyrelsen, ikke af Votra, og først når også kravene nedenfor er opfyldt.",
    requirements: [
      {
        title: "Helbred (gruppe 2)",
        text: "Du skal opfylde helbredskravene for \"Gruppe 2\" i taxibekendtgørelsens bilag 1. Det kræver et helbredstjek hos din læge, hvor især syn og hørelse vurderes. Har du helbredsmæssige forhold på lægeattesten, kan sagsbehandlingen tage længere tid, og kortet kan udstedes med vilkår, f.eks. brille- eller linsepåbud, påbud om høreapparat eller en individuelt fastsat tidsbegrænsning.",
      },
      {
        title: "Vandel og god skik",
        text: "Færdselsstyrelsen indhenter straffeattest til offentligt brug og eventuelt udskrift fra Det Centrale Kriminalregister. Den offentlige straffeattest går mindst 10 år tilbage. Strafbare forhold afskærer dig ikke automatisk: styrelsen foretager en konkret vurdering af forholdets karakter, gentagelser, dine personlige forhold og den tid, der er gået.",
      },
      {
        title: "Lægeattest",
        text: "Lægeattesten udfyldes på Færdselsstyrelsens særlige blanket og må ved indlevering ikke være over tre måneder gammel. Styrelsen kan kræve speciallægeerklæringer eller yderligere lægelige udtalelser, og du betaler selv for at fremskaffe dem.",
      },
      {
        title: "Foto og samtykke",
        text: "Du skal uploade et digitalt foto, der lever op til Rigspolitiets krav til pasfoto (nyt, vellignende, ensartet baggrund, ingen andre personer eller objekter, ikke en selfie, højst 10 MB). Du giver samtidig samtykke til, at styrelsen indhenter straffeattest og helbredsoplysninger.",
      },
      {
        title: "Har du boet i udlandet?",
        text: "Har du eller har du haft bopæl i udlandet, skal du selv kontakte politimyndighederne i dine tidligere opholdslande og bede dem sende et originalt eksemplar af straffeattesten direkte til Færdselsstyrelsen med post. Det er den enkeltstående ting, der oftest forsinker sagen.",
      },
    ],
    notes: [
      {
        label: "Gebyr",
        text: "1.700 kr. for chaufførkortet (betales til Færdselsstyrelsen) og 750 kr. for lægeattesten. Begge beløb er ud over kursusprisen.",
      },
      {
        label: "Gyldighed",
        text: "Et bestået kvalifikationskursus kan bruges som grundlag for chaufførkort i op til 10 år (taxibekendtgørelsens § 8).",
      },
      {
        label: "Fritagelse",
        text: "Har du gennemført Dansk Førstehjælpsråds kurser \"Færdselsrelateret førstehjælp\" og \"Førstehjælp ved hjertestop\" inden for de seneste 12 måneder, kan du fritages for førstehjælpsdelen.",
      },
    ],
    sources: [
      FS("Taxi-kvalifikationsuddannelsen, se Votra på udbyderlisten", "/privat/chauffoeruddannelser/taxi-kvalifikationsuddannelsen"),
      FS("Krav til chaufførkort (helbred, vandel, foto, samtykke)", "/erhverv/taxi/taxichauffoerer/krav-til-chauffoerkort"),
      FS("Lægeattest til chaufførkort", "/erhverv/taxi/taxichauffoerer/laegeattest"),
      TAXI_BEK,
    ],
  },

  bab: {
    processTitle: "Sådan kommer du i gang med flextrafik",
    steps: [
      {
        title: "Du finder ud af, hvilke moduler du mangler",
        text: "BAB-1 er introduktionen til offentlig servicetrafik, BAB-2 handler om sygdoms- og alderssvækkede passagerer, BAB-3A er liftbil, BAB-3B er trappemaskine, og BAB-4 er ajourføring. Kravet om forudgående moduler står ud for hvert modul i oversigten på denne side.",
      },
      {
        title: "Du tjekker, hvad trafikselskabet kræver",
        text: "Movia kræver, at alle chauffører, der kører flextrafik, har gennemført både \"Introduktionskursus til offentlig servicetrafik\" (BAB 1) og \"Befordring af sygdoms- og alderssvækkede passagerer\" (BAB 2). Skal du køre liftvogn eller befordre borgere med trappemaskine, kræves desuden \"Befordring af fysisk handicappede passagerer\"/\"Befordring af bevægelseshæmmede\" (BAB 3), det gælder vogntype 3, 4, 5, 6 og 7.",
      },
      {
        title: "Du skal også have førerkort til erhvervsmæssig personbefordring",
        text: "Ud over BAB-modulerne skal du som chauffør i flextrafik have et gyldigt førerkort til erhvervsmæssig personbefordring. Det kræver et chaufførkursus, der er godkendt af Færdselsstyrelsen, med undervisning i både teoretiske og praktiske forhold og en afsluttende prøve.",
      },
      {
        title: "Du får et kursusbevis pr. modul",
        text: "Hvert modul giver sit eget kursusbevis. BAB-4 er ajourføring og skal fornyes, beviset holder maksimalt 4 år. Som vognmand er du forpligtet til at sikre, at dine chauffører har gennemgået den krævede uddannelse.",
      },
    ],
    afterTitle: "Hvad kræver flextrafik-kørsel ud over kurset?",
    afterIntro:
      "BAB-modulerne er uddannelseskravet. Trafikselskabet og Færdselsstyrelsen stiller derudover krav til chaufføren og til vognmanden, kravene afhænger af vogntypen og den konkrete kontrakt.",
    requirements: [
      {
        title: "BAB 1 + BAB 2 er minimumskravet",
        text: "Alle chauffører, der kører flextrafik hos Movia, skal have gennemført både BAB 1 og BAB 2. Uden de to moduler må du ikke køre.",
      },
      {
        title: "Liftvogn og trappemaskine kræver BAB 3",
        text: "Skal du køre liftvogn eller befordre borgere med trappemaskine, skal du desuden have BAB 3. Hos Votra dækkes det af BAB-3A (liftbil) og BAB-3B (trappemaskine). Kravet gælder vogntype 3, 4, 5, 6 og 7.",
      },
      {
        title: "Gyldigt førerkort",
        text: "Du skal have et gyldigt førerkort til erhvervsmæssig personbefordring, som kræver et chaufførkursus godkendt af Færdselsstyrelsen med afsluttende prøve.",
      },
      {
        title: "Vognmandens ansvar",
        text: "Det er vognmanden, der har pligt til at sørge for, at chaufførerne har den krævede uddannelse. Dokumentationen skal kunne fremvises ved trafikselskabets kontrol.",
      },
    ],
    notes: [],
    sources: [
      { label: "Uddannelse af chauffører (Movia Flextrafik)", url: "https://moviatrafik.dk/flexvognmand/krav-til-vognmaend/uddannelse-af-chaufforer" },
      { label: "Krav til vognmænd (Movia Flextrafik)", url: "https://moviatrafik.dk/flexvognmand/krav-til-vognmaend" },
    ],
  },

  evs: {
    processTitle: "Sådan foregår evakueringsstolskurset",
    steps: [
      {
        title: "Du skal have BAB-3B først",
        text: "EVS bygger oven på trappemaskine-modulet. Du skal derfor have gennemført BAB-3B, før du kan deltage.",
      },
      {
        title: "Praktisk træning på én dag",
        text: "Kurset er praktisk: du træner at evakuere personer med nedsat mobilitet sikkert ned ad trapper ved brug af evakueringsstol.",
      },
      {
        title: "Du får et kursusbevis",
        text: "Efter gennemført kursus får du et kursusbevis i brug af evakueringsstol. Kurset afholdes i samarbejde med UCplus.",
      },
    ],
    afterTitle: "Hvad EVS ikke er",
    afterIntro:
      "Forkortelsen EVS forveksles ofte, både af mennesker og af AI-systemer. Her er den præcise afgrænsning.",
    requirements: [
      {
        title: "EVS er ikke en godstransportuddannelse",
        text: "EVS står for evakueringsstol. Kurset har intet med godskørsel, varebilstransport eller erhvervsvarebil at gøre. Skal du køre gods, er det vognmands- eller varebilschaufføruddannelserne, du skal se på.",
      },
      {
        title: "EVS er ikke det samme som BAB-3B",
        text: "BAB-3B kvalificerer til befordring af fysisk handicappede passagerer med trappemaskine og er en forudsætning for EVS. EVS er specialkurset i evakueringsstol oven på det.",
      },
    ],
    notes: [],
    sources: [],
  },
};

export const getCourseProcess = (slug) => COURSE_PROCESS[slug] || null;
